/**
 * Smart Copy Utility
 * 
 * Provides format-aware text copying for different social platforms.
 * Strips or converts markdown into platform-native formatting.
 */

/**
 * Copies text to clipboard with platform-specific formatting
 * @param {string} text - Raw markdown text to copy
 * @param {string} platform - Target platform (linkedin, twitter, facebook, etc.)
 * @returns {Promise<boolean>} - Success status
 */
export const smartCopy = async (text, platform = 'raw') => {
    if (!text) return false;

    let formattedText = text;

    // Platform-specific formatting rules
    switch (platform) {
        case 'linkedin':
            // LinkedIn: Remove markdown headers but keep the text
            formattedText = formattedText.replace(/^### (.*$)/gim, '$1');
            formattedText = formattedText.replace(/^## (.*$)/gim, '$1');
            formattedText = formattedText.replace(/^# (.*$)/gim, '$1');

            // LinkedIn: Convert markdown bold to unicode bold (optional but slick)
            // or just strip the asterisks since native LN bold is hard to reliably unicode
            formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '$1');

            // Remove italic markdown
            formattedText = formattedText.replace(/\*(.*?)\*/g, '$1');
            formattedText = formattedText.replace(/_(.*?)_/g, '$1');

            // Remove list asterisks if they are just bullets, keep clean spacing
            formattedText = formattedText.replace(/^\* /gim, '• ');
            formattedText = formattedText.replace(/^- /gim, '• ');
            break;

        case 'twitter':
            // Twitter: Strip all markdown
            formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '$1');
            formattedText = formattedText.replace(/\*(.*?)\*/g, '$1');
            formattedText = formattedText.replace(/^### (.*$)/gim, '$1');
            formattedText = formattedText.replace(/^## (.*$)/gim, '$1');
            formattedText = formattedText.replace(/^# (.*$)/gim, '$1');

            // Convert bullets to standard dashes
            formattedText = formattedText.replace(/^\* /gim, '- ');
            break;

        case 'facebook':
        case 'instagram':
            // Similar to LinkedIn, just clean text
            formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '$1');
            formattedText = formattedText.replace(/\*(.*?)\*/g, '$1');
            formattedText = formattedText.replace(/^### (.*$)/gim, '$1');
            formattedText = formattedText.replace(/^## (.*$)/gim, '$1');
            formattedText = formattedText.replace(/^# (.*$)/gim, '$1');
            formattedText = formattedText.replace(/^\* /gim, '• ');
            break;

        default:
            // Standard raw copy, keep markdown
            break;
    }

    try {
        await navigator.clipboard.writeText(formattedText.trim());
        return true;
    } catch (err) {
        console.error('Failed to copy text: ', err);
        return false;
    }
};
